import { Col, Space, Form, Row, Table, Input, Button } from 'antd';
import { t } from 'i18next';
import { ChangeEventHandler, FC, useCallback, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { FULL_DATE_FORMAT } from 'constants/dates';
import { USER_ROLES } from 'constants/users';
import { EllipsisOutlined, PlusOutlined } from '@ant-design/icons';
import { ADMIN_USER_ID } from 'constants/permissions';
import { fetchUsers } from 'services/api/users';
import { UserListRow } from 'types/api/users';

import useOrgEffect from 'hooks/useOrgEffect';
import { showErrorMessage } from 'utils/showErrorMessage';
import { ActionMenu, PageWrapper } from 'components/ui';
import { ChangeUserPassword, UsersModal } from '../components';

const Admins: FC = () => {
  const [isDetailsVisible, setDetailsVisible] = useState(false);
  const [detailsId, setDetailsId] = useState('');
  const [page, setPage] = useState(1);
  const [changePasswordId, setChangePasswordId] = useState('');
  const [, setSearch] = useState('');
  const timer = useRef<NodeJS.Timeout | null>(null);

  const [results, setResults] = useState<UserListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useOrgEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchUsers({
          page,
          limit: 10,
          role: USER_ROLES.admin
        });

        if (cancelled) return;
        setResults(res.results);
        setTotal(res.total);
      } catch (err) {
        if (!cancelled) showErrorMessage(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [page, refreshKey]);

  const refetch = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  const onSearchChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    e => {
      if (timer.current) {
        clearTimeout(timer.current);
      }

      timer.current = setTimeout(() => {
        setSearch(e.target.value);
      }, 500);
    },
    []
  );

  const onDetailsClose = useCallback(() => {
    setDetailsId('');
    setDetailsVisible(false);
  }, []);

  return (
    <PageWrapper
      title="Admin users"
      color="white"
      extra={[
        <Button
          type="primary"
          key="create-role"
          onClick={() => setDetailsVisible(true)}
        >
          <PlusOutlined />
          Create user
        </Button>
      ]}
    >
      <Row>
        <Col sm={8}>
          <Space>
            <Form.Item>
              <Input.Search
                placeholder={t('common.search')}
                onChange={onSearchChange}
              />
            </Form.Item>
          </Space>
        </Col>
      </Row>
      <Table
        dataSource={results}
        columns={[
          {
            title: 'ID',
            dataIndex: 'id',
            key: 'id'
          },
          {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render(_, record) {
              return `${record.first_name} ${record.last_name}`;
            }
          },
          {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            render: value => <a href={`mailto:${value}`}>{value}</a>
          },
          {
            title: 'Role',
            dataIndex: 'role',
            key: 'role',
            render(role) {
              return role?.name;
            }
          },
          {
            title: 'Created at',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (value: number) => dayjs(value).format(FULL_DATE_FORMAT)
          },
          {
            title: '',
            dataIndex: 'id',
            key: 'id',
            width: '5%',
            align: 'right',
            render: id =>
              id !== ADMIN_USER_ID && (
                <ActionMenu
                  data={[
                    {
                      title: 'Change password',
                      action: () => setChangePasswordId(id)
                    }
                  ]}
                  placement="bottomRight"
                  icon={<EllipsisOutlined />}
                />
              )
          }
        ]}
        loading={loading}
        rowKey="id"
        pagination={{
          total,
          onChange: p => setPage(p),
          showSizeChanger: false
        }}
      />
      {isDetailsVisible && (
        <UsersModal
          onClose={onDetailsClose}
          id={detailsId}
          role={USER_ROLES.admin}
          refetch={refetch}
        />
      )}
      {!!changePasswordId && (
        <ChangeUserPassword
          id={changePasswordId}
          onClose={() => setChangePasswordId('')}
        />
      )}
    </PageWrapper>
  );
};

export default Admins;
